import React, { useState, useEffect } from 'react';
import { Activity, Package, Warehouse, RefreshCw, Filter, Download, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4002';

// Helper to get admin auth token
const getAdminToken = () => {
  const authState = localStorage.getItem('bpv_admin_auth_state');
  if (!authState) throw new Error('Please login again');
  const { session } = JSON.parse(authState);
  if (!session?.access_token) throw new Error('Please login again');
  return session.access_token;
};

const ACTION_COLORS = {
  STOCK_ADDED:          'bg-green-100 text-green-700 border-green-200',
  STOCK_ADJUSTED:       'bg-blue-100 text-blue-700 border-blue-200',
  STOCK_DAMAGED:        'bg-red-100 text-red-700 border-red-200',
  TRANSFER_INITIATED:   'bg-purple-100 text-purple-700 border-purple-200',
  TRANSFER_RECEIVED:    'bg-indigo-100 text-indigo-700 border-indigo-200',
  INVENTORY_COUNT:      'bg-yellow-100 text-yellow-700 border-yellow-200',
  REPORT_GENERATED:     'bg-gray-100 text-gray-700 border-gray-200',
  LOGIN:                'bg-teal-100 text-teal-700 border-teal-200',
  LOGOUT:               'bg-slate-100 text-slate-700 border-slate-200',
};

const AdminActivityPage = () => {
  const [activities, setActivities] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterWarehouse, setFilterWarehouse] = useState('all');
  const [filterManager, setFilterManager] = useState('all');
  const [filterActionType, setFilterActionType] = useState('all');

  useEffect(() => {
    fetchWarehouses();
    fetchManagers();
  }, []);

  useEffect(() => {
    fetchActivities();
  }, [filterWarehouse, filterManager, filterActionType]);

  const fetchWarehouses = async () => {
    try {
      const token = getAdminToken();
      const response = await fetch(`${API_URL}/api/admin/warehouses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success) {
        setWarehouses(result.warehouses || []);
      }
    } catch (err) {
      console.error('Error fetching warehouses:', err);
    }
  };

  const fetchManagers = async () => {
    try {
      const token = getAdminToken();
      const response = await fetch(`${API_URL}/api/admin/warehouse-managers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success) {
        // Get unique managers
        const uniqueManagers = [];
        const seenIds = new Set();
        (result.assignments || []).forEach(assignment => {
          if (!seenIds.has(assignment.staff_id)) {
            seenIds.add(assignment.staff_id);
            uniqueManagers.push({
              id: assignment.staff_id,
              name: assignment.staff_name,
              employee_id: assignment.staff_employee_id
            });
          }
        });
        setManagers(uniqueManagers);
      }
    } catch (err) {
      console.error('Error fetching managers:', err);
    }
  };

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const token = getAdminToken();
      
      // Build query parameters
      const params = new URLSearchParams();
      if (filterWarehouse !== 'all') params.set('warehouse_id', filterWarehouse);
      if (filterManager !== 'all') params.set('staff_id', filterManager);
      if (filterActionType !== 'all') params.set('action_type', filterActionType);
      params.set('limit', '100');
      
      const response = await fetch(
        `${API_URL}/api/admin/activity/all?${params.toString()}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      const result = await response.json();
      if (result.success) {
        setActivities(result.activities || []);
      }
    } catch (err) {
      console.error('Error fetching activities:', err);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['Action Type', 'Warehouse', 'Manager', 'Product', 'Description', 'Quantity', 'Date', 'Time'];
    const rows = activities.map(a => [
      a.action_type,
      a.warehouse?.code || '',
      a.staff_name || '',
      a.product?.name || '',
      a.action_description || '',
      a.quantity_affected || '',
      new Date(a.performed_at).toLocaleDateString('en-IN'),
      new Date(a.performed_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
    ]);
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `warehouse-activities-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const actionTypes = [...new Set(activities.map(a => a.action_type))].filter(Boolean);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Warehouse Activity Log</h1>
          <p className="text-sm text-gray-500 mt-0.5">Monitor all warehouse manager actions across the system</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={exportToCSV}
            disabled={activities.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={16} />
            Export CSV
          </button>
          <button
            onClick={fetchActivities}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={16} className="text-gray-500" />
          <span className="text-sm font-semibold text-gray-700">Filters</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Warehouse Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Warehouse</label>
            <select
              value={filterWarehouse}
              onChange={(e) => setFilterWarehouse(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="all">All Warehouses</option>
              {warehouses.map(w => (
                <option key={w.id} value={w.id}>{w.code} – {w.name}</option>
              ))}
            </select>
          </div>

          {/* Manager Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Manager</label>
            <select
              value={filterManager}
              onChange={(e) => setFilterManager(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="all">All Managers</option>
              {managers.map(m => (
                <option key={m.id} value={m.id}>{m.name} ({m.employee_id})</option>
              ))}
            </select>
          </div>

          {/* Action Type Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Action Type</label>
            <select
              value={filterActionType}
              onChange={(e) => setFilterActionType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="all">All Actions</option>
              {actionTypes.map(type => (
                <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Activity Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full mx-auto mb-3" />
            <p className="text-gray-400 text-sm">Loading activities...</p>
          </div>
        ) : activities.length === 0 ? (
          <div className="p-12 text-center">
            <Activity size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">No activity recorded yet</p>
            <p className="text-xs text-gray-400 mt-1">Warehouse manager actions will appear here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                    Action Type
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                    Manager
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                    Warehouse
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-5 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                    Date & Time
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {activities.map(a => (
                  <tr key={a.id} className="hover:bg-gray-50 transition-colors">
                    {/* Action Type */}
                    <td className="px-5 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${ACTION_COLORS[a.action_type] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                        {a.product ? <Package size={12} /> : <Warehouse size={12} />}
                        {a.action_type?.replace(/_/g, ' ')}
                      </span>
                    </td>

                    {/* Manager */}
                    <td className="px-5 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Users size={14} className="text-orange-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {a.staff_name || '—'}
                          </div>
                          {a.staff_employee_id && (
                            <div className="text-xs text-gray-500">
                              {a.staff_employee_id}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Warehouse */}
                    <td className="px-5 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        {a.warehouse?.code || '—'}
                      </div>
                      {a.warehouse?.name && (
                        <div className="text-xs text-gray-500 truncate max-w-[150px]">
                          {a.warehouse.name}
                        </div>
                      )}
                    </td>

                    {/* Product */}
                    <td className="px-5 py-4">
                      {a.product ? (
                        <div className="flex items-center gap-2 max-w-[200px]">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {a.product.name}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>

                    {/* Description */}
                    <td className="px-5 py-4">
                      {a.action_description ? (
                        <div className="text-sm text-gray-700 max-w-[250px]">
                          {a.action_description}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>

                    {/* Quantity */}
                    <td className="px-5 py-4 text-center whitespace-nowrap">
                      {a.quantity_affected != null ? (
                        <span className={`inline-flex items-center justify-center px-3 py-1 rounded-lg text-sm font-bold ${
                          a.quantity_affected > 0 
                            ? 'bg-green-50 text-green-700 border border-green-200' 
                            : a.quantity_affected < 0
                            ? 'bg-red-50 text-red-700 border border-red-200'
                            : 'bg-gray-50 text-gray-700 border border-gray-200'
                        }`}>
                          {a.quantity_affected > 0 ? '+' : ''}{a.quantity_affected}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>

                    {/* Date & Time */}
                    <td className="px-5 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(a.performed_at).toLocaleDateString('en-IN', { 
                          day: '2-digit', 
                          month: 'short',
                          year: 'numeric'
                        })}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(a.performed_at).toLocaleTimeString('en-IN', { 
                          hour: '2-digit', 
                          minute: '2-digit',
                          hour12: true
                        })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      {activities.length > 0 && (
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Activity size={18} className="text-orange-600" />
              <span className="text-sm font-semibold text-gray-900">
                Total Activities: {activities.length}
              </span>
            </div>
            <div className="flex items-center gap-6 text-xs">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                <span className="text-gray-600">
                  Added: {activities.filter(a => a.quantity_affected > 0).length}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                <span className="text-gray-600">
                  Reduced: {activities.filter(a => a.quantity_affected < 0).length}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                <span className="text-gray-600">
                  Unique Managers: {new Set(activities.map(a => a.staff_id).filter(Boolean)).size}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminActivityPage;
